<template>
  <v-card variant="flat" class="soc-power-curve pa-4 rounded-xl" :loading="loadingElink">
    <div class="chart-header">
      <span class="title">今日电站SOC-功率跟踪分析曲线</span>
      <div class="legend-container">
        <!-- Legend can be custom or handled by echarts. Custom looks better sometimes but let's stick to echarts for simplicity unless specified -->
      </div>
    </div>
    <div class="chart-container" ref="chartRef"></div>
  </v-card>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { initChart } from '@/utils/index'
import * as echarts from 'echarts'
import dayjs from 'dayjs'

// 接收父组件传递的功率跟踪数据
const props = defineProps({
  powerTrackingData: {
    type: Array,
    default: () => []
  },
  loadingElink: {
    type: Boolean,
    default: false
  }
})

const chartRef = ref(null)
let chartInstance = null

const updateChart = (data=[]) => {
  if (!chartRef.value) return

  // 处理返回的数据
  let timeLabels = []
  let socData = data.map(item => [item.record_time, item.batterysystemsoc.toFixed(2)])
  let powerData = data.map(item => [item.record_time, item.pcspower_dc.toFixed(2)])

  const min = dayjs().startOf('day').toDate()
  const max = dayjs().endOf('day').toDate()

  /**
   * @type {import('echarts').EChartsOption}
   */
  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      // formatter: (params) => {
      //   let result = `${params[0].axisValue}<br/>`
      //   params.forEach(param => {
      //     if (param.seriesName === 'SOC(%)') {
      //       result += `${param.marker} ${param.seriesName}: ${param.value}<br/>`
      //     } else {
      //       result += `${param.marker} ${param.seriesName}: ${param.value}<br/>`
      //     }
      //   })
      //   return result
      // }
    },
    legend: {
      data: ['SOC(%)', '功率(kW)'],
      top: '0%',
      right: '0%',
      icon: 'circle',
      itemGap: 20
    },
    grid: [{
      top: '15%',
      left: '1%',
      right: '1%',
      height: '30%',
      // containLabel: true
    },
    {
      top: '55%',
      left: '1%',
      right: '1%',
      bottom: '10%',
      // containLabel: true
    }
    ],
    xAxis: [{
      type: 'time',
      gridIndex: 0,
      boundaryGap: false,
      min: min,
      max: max,
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#999'
      }
    }, 
    {
      // show: false,
      type: 'time',
      gridIndex: 1,
      boundaryGap: false,
      min: min,
      max: max,
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#999'
      }
    }],
    yAxis: [
      {
        gridIndex: 0,
        type: 'value',
        name: '功率(kW)',
        nameLocation: 'middle',
        nameTextStyle: {
          color: '#999'
        },
        axisLabel: {
          formatter: '{value}',
          color: '#999'
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#eee'
          }
        }
      },
      {
        gridIndex: 1,
        type: 'value',
        name: 'SOC(%)',
        min: 0,
        max: 100,
        interval: 20,
        nameLocation: 'middle',
        nameTextStyle: {
          color: '#999',
          // padding: [0, 0, 0, 0]
        },
        axisLabel: {
          formatter: '{value}%',
          color: '#999'
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#eee'
          }
        }
      },

    ],
    axisPointer: {
      link: {
        xAxisIndex: 'all'
      }
    },
    series: [
      {
        xAxisIndex: 0,
        yAxisIndex: 0,
        name: '功率(kW)',
        type: 'line',
        smooth: true,
        showSymbol: false,
        itemStyle: {
          color: '#39D8BB'
        },
        data: powerData
      },
      {
        xAxisIndex: 1,
        yAxisIndex: 1,
        name: 'SOC(%)',
        type: 'line',
        smooth: true,
        showSymbol: false,
        itemStyle: {
          color: '#8DD3F5'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(141, 211, 245, 0.5)' },
            { offset: 1, color: 'rgba(141, 211, 245, 0.01)' }
          ])
        },
        data: socData
      },

    ]
  }

  chartInstance = initChart(chartRef.value, undefined, option)
}

// 监听数据变化，更新图表
watch(
  () => props.powerTrackingData,
  (newData) => {
    updateChart(newData || [])
  },
  { deep: true }
)

onMounted(() => {
  nextTick(() => {
    updateChart(props.powerTrackingData)
  })
})

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>

<style scoped lang="scss">
.soc-power-curve {
  width: 100%;
  height: 600px;
  /* Adjust height as needed */
  background: rgba(255, 255, 255, 1);
  // border-radius: 16px;
  // padding: 16px;
  display: flex;
  flex-direction: column;
  // margin-top: 16px;
}

.chart-header {
  margin-bottom: 20px;
}

.title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 16px;
  color: rgba(46, 46, 46, 1);
  font-weight: 500;
}

.chart-container {
  flex: 1;
  width: 100%;
  min-height: 0;
  /* Important for flex child */
}
</style>
